import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getPublicBlogs } from "../services/publicService";
import { getBreadcrumbSchema } from "./navigation/breadcrumbSchema";
import SEO from "./seo/SEO";
import "./BlogPage.css";

const CRAWLER_FRIENDLY_BLOGS = [
  {
    id: "crawler-ai",
    title: "AI, Software, and Secure Digital Transformation",
    content:
      "BlackRoth shares practical perspectives on artificial intelligence, software engineering, cybersecurity, cloud services, automation, and digital transformation for modern businesses.",
    featured_image: "",
  },
  {
    id: "crawler-services",
    title: "Technology Services for Growing Teams",
    content:
      "Explore insights about web development, mobile development, UI/UX, data science, cloud security, edge computing, IoT integration, and business process support.",
    featured_image: "",
  },
  {
    id: "crawler-hr-payroll",
    title: "HR, Payroll, and Operations Insights",
    content:
      "Read updates and articles covering payroll services, HR operations, workforce processes, recruitment support, and business operations built for reliability.",
    featured_image: "",
  },
];

const splitHeadingLetters = (text = "", prefix = "blog-heading") =>
  text.split("").map((character, index) => (
    <span
      key={`${prefix}-${index}`}
      className={`blog-heading-letter${character === " " ? " is-space" : ""}`}
      style={{ "--letter-index": index }}
      aria-hidden="true"
    >
      {character === " " ? "\u00A0" : character}
    </span>
  ));

const BlogPage = () => {
  const location = useLocation();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isHeadingVisible, setIsHeadingVisible] = useState(false);

  //  NEW STATE (for Read More)
  const [expandedBlogs, setExpandedBlogs] = useState({});

  const toggleReadMore = (id) => {
    setExpandedBlogs((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const from = new URLSearchParams(location.search).get("from");
  const isFromNewsroom =
    from === "newsroom" || location.pathname.startsWith("/newsroom/");
  const backPath = isFromNewsroom ? "/newsroom" : "/";
  const backLabel = isFromNewsroom
    ? "\u2190 Back to Newsroom"
    : "\u2190 Back to Home";

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const res = await getPublicBlogs();
        setBlogs(res?.blogs || []);
      } catch (err) {
        console.error("Fetch Blogs Error:", err);
        setError("Failed to load blogs. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();

    const headingTimer = window.setTimeout(() => {
      setIsHeadingVisible(true);
    }, 80);

    window.scrollTo(0, 0);

    return () => {
      window.clearTimeout(headingTimer);
    };
  }, []);

  // Create breadcrumb items for blog page
  const breadcrumbItems = [
    { label: "Home", to: "/" },
    { label: "Newsroom", to: "/newsroom" },
    { label: "Blog" }
  ];
  const breadcrumbSchema = getBreadcrumbSchema(breadcrumbItems);
  const hasLiveBlogs = !loading && blogs.length > 0;
  const visibleBlogs = hasLiveBlogs ? blogs : CRAWLER_FRIENDLY_BLOGS;

  return (
    <div className="blog-wrapper">
      <SEO schema={breadcrumbSchema} />
      <div className="blog-container blog-page-animated">
        <div className="back-button-fixed">
          <Link to={backPath}>{backLabel}</Link>
        </div>

        <div className="blog-hero">
          <h1
            className={`blog-animated-heading ${
              isHeadingVisible ? "is-visible" : ""
            }`}
            aria-label="BlackRoth Blog"
          >
            {splitHeadingLetters("BlackRoth Blog", "blog-main-heading")}
          </h1>
        </div>

        {loading && <p className="blog-status">Loading latest blogs...</p>}
        {error && <p className="blog-error">{error}</p>}

        {visibleBlogs.map((blog) => {
            const mediaList = Array.isArray(blog.featured_image)
              ? blog.featured_image
              : blog.featured_image
              ? [blog.featured_image]
              : [];

            //  Logic for preview & expand
            const isExpanded = expandedBlogs[blog.id];
            const previewLength = 2000;
            const isLong = blog.content?.length > previewLength;

            return (
              <div className="blog-post" key={blog.id}>
                <h2 className="blog-title">{blog.title}</h2>

                {/* 🎯 MULTIPLE MEDIA RENDERING */}
                {mediaList.length > 0 ? (
                  mediaList.map((mediaUrl, index) => {
                    const isVideo =
                      /\.(mp4|webm|ogg|mov)$/i.test(mediaUrl);

                    return isVideo ? (
                      <video
                        key={`video-${index}`}
                        className="blog-video"
                        controls
                        preload="metadata"
                        style={{
                          width: "100%",
                          marginTop: "10px",
                          borderRadius: "8px",
                        }}
                      >
                        <source src={mediaUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <img
                        key={`image-${index}`}
                        src={mediaUrl}
                        alt={`blog-${index}`}
                        className="blog-image"
                        loading="lazy"
                        style={{
                          marginTop: "10px",
                          borderRadius: "8px",
                        }}
                      />
                    );
                  })
                ) : (
                  <img
                    src="/fallback.jpg"
                    alt="fallback"
                    className="blog-image"
                  />
                )}

                {/*  CONTENT WITH EXPAND */}
                <p
                  className="blog-content"
                  style={{ whiteSpace: "pre-line" }}
                >
                  {isExpanded
                    ? blog.content
                    : blog.content?.slice(0, previewLength) +
                      (isLong ? "..." : "")}
                </p>

                {/*  READ MORE BUTTON */}
                {isLong && (
                  <button
                    className="read-more-btn"
                    onClick={() => toggleReadMore(blog.id)}
                  >
                    {isExpanded ? "Show Less ↑" : "Read More →"}
                  </button>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default BlogPage;
